import { P3Campaign as IP3Campaign } from 'models/interfaces';
import P3Campaign from 'models/P3Campaign';
import { P3Configuration as IP3Configuration } from 'models/interfaces/P3Configurations';
import P3Configuration from 'models/P3Configuration';
import { ApolloClient } from 'apollo-client';
import uuid from 'uuid/v4';
import {
  saveP3Configurations,
  duplicateP3Configuration
} from 'views/components/P3Templates/P3TemplateEditConfiguration';
import { SAVE_P3_CAMPAIGN_MUTATION } from 'containers/P3CampaignContainer';
import { refetchQueries } from 'views/components/P3Campaigns/P3CampaignEditConfiguration';

class P3CampaignSaveMachine {
  private apolloClient: ApolloClient<any>;
  private campaignModule: IP3Campaign;
  private newConfigurationId: string = '';

  constructor(apolloClient: ApolloClient<any>, campaignModule: IP3Campaign) {
    this.apolloClient = apolloClient;
    this.campaignModule = campaignModule;
    // create new configurationId
    this.newConfigurationId = uuid();
  }

  /**
   * The campaignModule's configuration can be considered new if the p3_configuration object
   * does not exist on the campaignModule
   */
  public configurationIsNew = () =>
    this.campaignModule.p3_configuration &&
    P3Configuration.configurationIsNew(this.campaignModule.p3_configuration);

  public async save() {
    try {
      // User opts to create a new configuration
      if (this.configurationIsNew()) {
        return this.saveNewConfiguration();
      }

      // User opted to apply a template
      if (P3Campaign.configurationIsTemplate(this.campaignModule)) {
        console.log(`enter to save from template`, this.campaignModule);
        return this.saveFromTemplate();
      }

      // they're just updating the campaign or config
      return this.updateExistingRecords();
    } catch (error) {
      console.log('Failed to mutate rp campaign', error);
      Promise.reject(false);
    }
    Promise.resolve(true);
  }

  private async saveNewConfiguration() {
    // build new P3Configuration object and make a postModel from it
    const p3Configuration: IP3Configuration = P3Configuration.fromP3Campaign(this.campaignModule, {
      configurationId: this.newConfigurationId
    }).makeP3ConfigurationInput();

    // save new p3Configuration
    console.log(`save new configuration`);

    const {
      data: { saveP3Configuration }
    } = await saveP3Configurations(this.apolloClient, p3Configuration);

    // build new p3Campaign with campaignModule and newly saved configuration
    const p3CampaignModel: IP3Campaign = P3Campaign.fromP3CampaignConfiguration(
      this.campaignModule,
      saveP3Configuration
    ).makeP3CampaignInput();

    // save p3Campaign as usual
    await this.apolloClient.mutate({
      mutation: SAVE_P3_CAMPAIGN_MUTATION,
      variables: { campaign: p3CampaignModel },
      refetchQueries: refetchQueries(
        this.campaignModule.p3_campaign_id,
        this.campaignModule.advertiser ? this.campaignModule.advertiser.tgrams_id : ''
      )
    });
  }

  private async saveFromTemplate() {
    if (!this.campaignModule.p3_configuration) {
      throw new TypeError('p3_configuration is missing from campaignModule');
    }

    // get templateId from p3Configuration
    const templateId = this.campaignModule.p3_configuration.p3_configuration_id;

    // duplicate the template
    const duplicateResponse: any = await duplicateP3Configuration(
      this.apolloClient,
      templateId,
      this.newConfigurationId,
      this.campaignModule.p3_campaign_id,
      this.campaignModule.advertiser ? this.campaignModule.advertiser.tgrams_id : '',
      undefined,
      undefined
    );

    // get new p3Configuration from duplicateResponse
    const duplicatedP3Config: IP3Configuration = duplicateResponse.data.duplicateP3Configuration;

    // update the name and dates of the duplicated template
    duplicatedP3Config.name = this.campaignModule.name;
    duplicatedP3Config.start_date = this.campaignModule.start_date;
    duplicatedP3Config.end_date = this.campaignModule.end_date;

    // save the new duplicatedP3Config after updating the name and dates
    const saveResponse = await saveP3Configurations(this.apolloClient, duplicatedP3Config);

    const p3Configuration: IP3Configuration = saveResponse.data.saveP3Configuration;

    // build a new P3Campaign from the campaignModule and new p3Configuration
    const p3CampaignModel = P3Campaign.fromP3CampaignConfiguration(
      this.campaignModule,
      p3Configuration
    ).makeP3CampaignInput();

    // finally, save the p3Campaign
    return this.apolloClient.mutate({
      mutation: SAVE_P3_CAMPAIGN_MUTATION,
      variables: { campaign: p3CampaignModel },
      refetchQueries: refetchQueries(
        this.campaignModule.p3_campaign_id,
        this.campaignModule.advertiser ? this.campaignModule.advertiser.tgrams_id : ''
      )
    });
  }

  private async updateExistingRecords() {
    if (!this.campaignModule.p3_configuration) {
      throw new TypeError('p3_configuration is missing from campaignModule');
    }

    // build config save model
    const p3ConfigurationModel: IP3Configuration = P3Configuration.makeP3ConfigurationInput(
      this.campaignModule.p3_configuration
    );

    // save config
    await saveP3Configurations(this.apolloClient, p3ConfigurationModel);

    // build campaign model
    const p3CampaignModel: IP3Campaign = P3Campaign.makeP3CampaignInput(this.campaignModule);

    // save campaign
    return await this.apolloClient.mutate({
      mutation: SAVE_P3_CAMPAIGN_MUTATION,
      variables: { campaign: p3CampaignModel },
      refetchQueries: refetchQueries(
        this.campaignModule.p3_campaign_id,
        this.campaignModule.advertiser ? this.campaignModule.advertiser.tgrams_id : ''
      )
    });
  }
}

export default P3CampaignSaveMachine;
